import type { TFunction } from 'i18next';
import { themeBootstrapErrorResponseSchema } from '@aica/api-types/schemas';
import { ApiError, apiErrorBody } from '@/lib/fetch-utils';

/** Which request failed: the words differ — preparing a proposal is not adding themes. */
export type BootstrapFailedRequest = 'generate' | 'approve';

/**
 * What to say about a failed starter-proposal request (#845 PR 4) — by the
 * refusal's `code`; a bare 403 is the role, anything else «try again». An
 * approval failure never says «could not prepare the proposal» (the proposal
 * was prepared) and never nudges a paid retry: it says the themes were not
 * added, and a renewed proposal (`bootstrap_changed`) says the edits were not
 * applied to it.
 */
export async function bootstrapErrorMessage(
  error: unknown,
  t: TFunction,
  request: BootstrapFailedRequest = 'generate'
): Promise<string> {
  const generic =
    request === 'approve'
      ? t('dashboard:voicePage.themes.bootstrap.errors.approveGeneric')
      : t('dashboard:voicePage.themes.bootstrap.errors.generic');
  const raw = await apiErrorBody(error);
  if (raw?.code === 'capability_required') {
    return t('dashboard:voicePage.themes.errors.narrowGrant');
  }
  const parsed = raw ? themeBootstrapErrorResponseSchema.safeParse(raw) : null;
  const status = error instanceof ApiError ? error.status : undefined;
  if (!parsed?.success) {
    return status === 403 ? t('dashboard:voicePage.themes.errors.forbidden') : generic;
  }
  switch (parsed.data.code) {
    case 'over_limit':
      return t('dashboard:voicePage.themes.bootstrap.errors.overLimit');
    case 'temporarily_unavailable':
    case 'bootstrap_failed':
      return generic;
    case 'bootstrap_changed':
      return request === 'approve'
        ? t('dashboard:voicePage.themes.bootstrap.errors.approveChanged')
        : t('dashboard:voicePage.themes.bootstrap.errors.changed');
    case 'bootstrap_already_approved':
      return t('dashboard:voicePage.themes.bootstrap.errors.alreadyApproved');
    case 'bootstrap_not_ready':
      return t('dashboard:voicePage.themes.bootstrap.errors.notReady');
    case 'theme_invalid':
      return t('dashboard:voicePage.themes.bootstrap.errors.invalid');
    case 'themes_disabled':
    case 'feature_disabled':
      return t('dashboard:voicePage.themes.errors.themesDisabled');
    case 'tier_not_eligible':
      return t('dashboard:voicePage.themes.errors.locked');
  }
}
